import { useSyncExternalStore } from 'react';
import { uid } from '../lib/id';
import { createNote, duplicateNote, isEmptyNote, noteText, plainText } from '../notes/model';
import { isQuotaError } from '../storage/db';
import { defaultMeta, defaultSettings } from '../storage/defaults';
import { journalClear, journalPut, journalRecover } from '../storage/journal';
import * as repo from '../storage/repo';
import type { Design, Meta, Note, Schedule, Settings, Snapshot } from '../storage/schemas';
import { resolveActiveNote, type ActiveResolution } from '../scheduling/activeNote';
import type { ReminderPayload } from '../notifications/adapter';

export type SaveStatus = 'saved' | 'pending' | 'saving' | 'error';

export interface Toast {
  id: string;
  text: string;
  tone: 'info' | 'error' | 'success';
  action?: { label: string; run: () => void };
}

export interface AppState {
  ready: boolean;
  fatal: string | null;
  notes: Record<string, Note>;
  designs: Design[];
  schedules: Schedule[];
  settings: Settings;
  meta: Meta;
  saveStatus: Record<string, SaveStatus>;
  saveError: string | null;
  quarantine: repo.QuarantinedRecord[];
  toasts: Toast[];
  inbox: (ReminderPayload & { receivedAt: number; missed?: boolean })[];
  active: ActiveResolution;
  recovered: number;
}

const SAVE_DEBOUNCE_MS = 400;

function initialState(): AppState {
  return {
    ready: false,
    fatal: null,
    notes: {},
    designs: [],
    schedules: [],
    settings: defaultSettings(),
    meta: defaultMeta(),
    saveStatus: {},
    saveError: null,
    quarantine: [],
    toasts: [],
    inbox: [],
    active: { noteId: null, scheduleId: null, until: null, source: 'manual' },
    recovered: 0,
  };
}

type Listener = () => void;

class Store {
  private state: AppState = initialState();
  private listeners = new Set<Listener>();
  private timers = new Map<string, ReturnType<typeof setTimeout>>();
  private inflight = new Map<string, Promise<void>>();
  private lastSnapshot = new Map<string, { at: number; text: string }>();
  private channel: BroadcastChannel | null = null;
  now: () => number = () => Date.now();

  get(): AppState {
    return this.state;
  }

  subscribe = (l: Listener): (() => void) => {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  };

  private set(patch: Partial<AppState> | ((s: AppState) => Partial<AppState>)): void {
    const p = typeof patch === 'function' ? patch(this.state) : patch;
    this.state = { ...this.state, ...p };
    this.listeners.forEach((l) => l());
  }

  /** Reset to a pristine state (tests). */
  reset(): void {
    this.timers.forEach((t) => clearTimeout(t));
    this.timers.clear();
    this.inflight.clear();
    this.lastSnapshot.clear();
    this.channel?.close();
    this.channel = null;
    this.state = initialState();
  }

  // ------------------------------------------------------------ lifecycle

  async init(): Promise<void> {
    try {
      const data = await repo.loadAll();
      const notes: Record<string, Note> = Object.fromEntries(data.notes.map((n) => [n.id, n]));
      // Crash recovery: anything journaled but not persisted is newer than the database copy.
      const recovered = journalRecover(new Map(Object.entries(notes)));
      for (const n of recovered) notes[n.id] = n;
      let meta = data.meta;
      if (!meta.activeNoteId || !notes[meta.activeNoteId] || notes[meta.activeNoteId].deletedAt) {
        const candidate = Object.values(notes)
          .filter((n) => !n.deletedAt && !n.archived)
          .sort((a, b) => b.updatedAt - a.updatedAt)[0];
        meta = { ...meta, activeNoteId: candidate?.id ?? null };
      }
      // First launch (or everything deleted): start with one blank note before the UI renders.
      let created: Note | null = null;
      if (!meta.activeNoteId) {
        created = createNote({}, data.settings);
        notes[created.id] = created;
        meta = { ...meta, activeNoteId: created.id };
      }
      this.set({
        ready: true,
        notes,
        designs: data.designs,
        schedules: data.schedules,
        settings: data.settings,
        meta,
        quarantine: data.quarantine,
        recovered: recovered.length,
        active: { noteId: meta.activeNoteId, scheduleId: null, until: null, source: 'manual' },
      });
      for (const n of recovered) this.scheduleSave(n.id);
      if (created) this.scheduleSave(created.id, 0);
      await repo.saveMeta(meta);
      this.refreshActive();
      if (recovered.length) this.toast(`Recovered ${recovered.length} unsaved change${recovered.length > 1 ? 's' : ''}.`, 'success');
      this.listenToOtherTabs();
    } catch (err) {
      this.set({
        ready: true,
        fatal:
          err instanceof Error && err.name === 'StorageUnavailableError'
            ? 'MemoNest cannot store notes in this browser mode (private browsing or blocked site data). Notes you write now will not be kept.'
            : `MemoNest could not open its local database: ${(err as Error)?.message ?? err}`,
      });
      // Still let the user write in memory.
      const n = createNote({}, this.state.settings);
      this.set((s) => ({ notes: { ...s.notes, [n.id]: n }, meta: { ...s.meta, activeNoteId: n.id }, active: { noteId: n.id, scheduleId: null, until: null, source: 'manual' } }));
    }
  }

  private listenToOtherTabs(): void {
    if (typeof BroadcastChannel === 'undefined') return;
    this.channel = new BroadcastChannel('memonest');
    this.channel.onmessage = async (e: MessageEvent<{ type: string; id?: string }>) => {
      if (e.data?.type === 'note' && e.data.id) {
        const id = e.data.id;
        if (this.timers.has(id) || this.inflight.has(id)) return; // local edits pending; conflict handling covers it
        const fresh = await repo.getNote(id);
        if (fresh) this.set((s) => ({ notes: { ...s.notes, [id]: fresh } }));
      } else if (e.data?.type === 'reload') {
        const data = await repo.loadAll();
        this.set({ notes: Object.fromEntries(data.notes.map((n) => [n.id, n])), designs: data.designs, schedules: data.schedules, settings: data.settings });
        this.refreshActive();
      }
    };
  }

  private broadcast(msg: { type: string; id?: string }): void {
    try {
      this.channel?.postMessage(msg);
    } catch {
      /* ignore */
    }
  }

  // ------------------------------------------------------------ notes

  createNote(init: Partial<Note> = {}): Note {
    const n = createNote(init, this.state.settings);
    this.set((s) => ({ notes: { ...s.notes, [n.id]: n } }));
    this.scheduleSave(n.id, 0);
    return n;
  }

  /** Apply an edit. The change is visible immediately; persistence is debounced. */
  updateNote(id: string, fn: (n: Note) => Note, opts: { touch?: boolean } = {}): void {
    const cur = this.state.notes[id];
    if (!cur) return;
    const next = fn(cur);
    if (next === cur) return;
    const updated = opts.touch === false ? next : { ...next, updatedAt: this.now() };
    this.set((s) => ({ notes: { ...s.notes, [id]: updated }, saveStatus: { ...s.saveStatus, [id]: 'pending' } }));
    journalPut(updated);
    this.scheduleSave(id);
  }

  private scheduleSave(id: string, delay = SAVE_DEBOUNCE_MS): void {
    const t = this.timers.get(id);
    if (t) clearTimeout(t);
    this.timers.set(
      id,
      setTimeout(() => {
        this.timers.delete(id);
        void this.persist(id);
      }, delay),
    );
  }

  /** Save every pending note now (page hide, navigation, tests). */
  async flush(): Promise<void> {
    const ids = [...this.timers.keys()];
    ids.forEach((id) => {
      clearTimeout(this.timers.get(id)!);
      this.timers.delete(id);
    });
    await Promise.all([...ids.map((id) => this.persist(id)), ...this.inflight.values()]);
  }

  private async persist(id: string): Promise<void> {
    const prev = this.inflight.get(id);
    if (prev) await prev;
    const p = this.doPersist(id).finally(() => this.inflight.delete(id));
    this.inflight.set(id, p);
    return p;
  }

  private async doPersist(id: string): Promise<void> {
    const note = this.state.notes[id];
    if (!note || this.state.fatal) return;
    this.set((s) => ({ saveStatus: { ...s.saveStatus, [id]: 'saving' } }));
    try {
      let result = await repo.saveNote(note);
      if (!result.ok) {
        // Another tab saved a newer revision. Keep ours (the user is looking at it) and file theirs in history.
        const theirs = result.conflict;
        await repo.addSnapshot(this.snapshotOf(theirs, 'conflict', 'Version from another window'));
        result = await repo.saveNote({ ...note, rev: theirs.rev });
        this.toast('This note was also changed in another window. That version was saved to its history.', 'info');
      }
      if (result.ok) {
        const saved = result.note;
        // Only adopt the new rev; keep any edits typed while the write was in flight.
        this.set((s) => {
          const live = s.notes[id];
          const stillPending = this.timers.has(id);
          return {
            notes: { ...s.notes, [id]: live ? { ...live, rev: saved.rev } : saved },
            saveStatus: { ...s.saveStatus, [id]: stillPending ? 'pending' : 'saved' },
            saveError: null,
          };
        });
        if (!this.timers.has(id)) journalClear(id, saved.rev);
        this.broadcast({ type: 'note', id });
        await this.maybeAutoSnapshot(saved);
        if (saved.rev === 1) void repo.requestPersistence();
      }
    } catch (err) {
      const msg = isQuotaError(err)
        ? 'Storage is full. Free space or export a backup — your latest text is kept in a recovery copy.'
        : `Could not save: ${(err as Error)?.message ?? err}`;
      this.set((s) => ({ saveStatus: { ...s.saveStatus, [id]: 'error' }, saveError: msg }));
    }
  }

  retrySave(id: string): void {
    void this.persist(id);
  }

  setActive(id: string | null): void {
    const meta = { ...this.state.meta, activeNoteId: id };
    this.set({ meta });
    this.refreshActive();
    void repo.saveMeta(meta).catch(() => undefined);
  }

  /** Recompute the scheduled active note (called by the scheduler tick and after edits). */
  refreshActive(): void {
    const s = this.state;
    const exists = (id: string) => !!s.notes[id] && !s.notes[id].deletedAt;
    const r = resolveActiveNote(this.now(), s.schedules, s.meta.activeNoteId, exists);
    const prev = s.active;
    if (prev.noteId !== r.noteId || prev.scheduleId !== r.scheduleId || prev.until !== r.until || prev.source !== r.source) this.set({ active: r });
  }

  async trashNote(id: string): Promise<void> {
    const note = this.state.notes[id];
    if (!note) return;
    if (isEmptyNote(note)) {
      await this.purgeNote(id);
      return;
    }
    await repo.addSnapshot(this.snapshotOf(note, 'delete', 'Before delete')).catch(() => undefined);
    this.updateNote(id, (n) => ({ ...n, deletedAt: this.now(), pinned: false }));
    if (this.state.meta.activeNoteId === id) this.setActive(this.nextActiveCandidate(id));
  }

  private nextActiveCandidate(excluding: string): string | null {
    return (
      Object.values(this.state.notes)
        .filter((n) => n.id !== excluding && !n.deletedAt && !n.archived)
        .sort((a, b) => b.updatedAt - a.updatedAt)[0]?.id ?? null
    );
  }

  /** Toggle a checklist line from anywhere (history, preview). */
  toggleLine(noteId: string, lineId: string): void {
    const line = this.state.notes[noteId]?.lines.find((l) => l.id === lineId);
    if (!line) return;
    this.updateNote(noteId, (n) => ({ ...n, lines: n.lines.map((l) => (l.id === lineId ? { ...l, checked: !l.checked } : l)) }));
    if (!line.checked) this.afterChecked(noteId, lineId);
  }

  /** Reference "Delete line when checked": snapshot the note, then remove the line one second later if still checked. */
  afterChecked(noteId: string, lineId: string): void {
    if (!this.state.settings.deleteCheckedLines) return;
    const note = this.state.notes[noteId];
    if (note) void repo.addSnapshot(this.snapshotOf(note, 'auto', 'Before checked line was removed')).catch(() => undefined);
    setTimeout(() => {
      const cur = this.state.notes[noteId];
      const l = cur?.lines.find((x) => x.id === lineId);
      if (!cur || !l?.checked) return;
      const rest = cur.lines.filter((x) => x.id !== lineId);
      this.updateNote(noteId, (n) => ({ ...n, lines: rest.length ? rest : [{ id: uid('l'), text: '', kind: 'check', checked: false }] }));
    }, 1000);
  }

  restoreNote(id: string): void {
    this.updateNote(id, (n) => ({ ...n, deletedAt: null, archived: false }));
  }

  async purgeNote(id: string): Promise<void> {
    const t = this.timers.get(id);
    if (t) clearTimeout(t);
    this.timers.delete(id);
    await this.inflight.get(id);
    journalClear(id);
    this.set((s) => {
      const notes = { ...s.notes };
      delete notes[id];
      return { notes, schedules: s.schedules.filter((x) => x.noteId !== id) };
    });
    if (this.state.meta.activeNoteId === id) this.setActive(this.nextActiveCandidate(id));
    await repo.purgeNote(id).catch(() => undefined);
    this.broadcast({ type: 'reload' });
  }

  async emptyTrash(): Promise<number> {
    const doomed = Object.values(this.state.notes).filter((n) => n.deletedAt);
    for (const n of doomed) await this.purgeNote(n.id);
    await repo.collectGarbageAssets(this.referencedAssets()).catch(() => 0);
    return doomed.length;
  }

  referencedAssets(): Set<string> {
    const keep = new Set<string>();
    for (const n of Object.values(this.state.notes)) for (const i of n.images) keep.add(i.assetId);
    for (const f of this.state.settings.customFonts) keep.add(f.assetId);
    if (this.state.settings.lockPreview.customWallpaperAssetId) keep.add(this.state.settings.lockPreview.customWallpaperAssetId);
    return keep;
  }

  duplicate(id: string): Note | null {
    const n = this.state.notes[id];
    if (!n) return null;
    const copy = duplicateNote(n);
    this.set((s) => ({ notes: { ...s.notes, [copy.id]: copy } }));
    this.scheduleSave(copy.id, 0);
    return copy;
  }

  // ------------------------------------------------------------ history / snapshots

  snapshotOf(note: Note, reason: Snapshot['reason'], label?: string): Snapshot {
    const text = noteText(note);
    return { id: uid('h'), noteId: note.id, createdAt: this.now(), reason, label, text, chars: plainText(note).length, note: structuredClone(note) };
  }

  private async maybeAutoSnapshot(note: Note): Promise<void> {
    const every = this.state.settings.history.autoSnapshotMinutes * 60_000;
    const last = this.lastSnapshot.get(note.id);
    const text = JSON.stringify([note.lines, note.images.length, note.strokes.length, note.designId]);
    if (last && (this.now() - last.at < every || last.text === text)) return;
    if (!last) {
      // First save this session: seed from stored history so a reload does not create a duplicate.
      const existing = await repo.listSnapshots(note.id).catch(() => []);
      if (existing[0] && this.now() - existing[0].createdAt < every) {
        this.lastSnapshot.set(note.id, { at: existing[0].createdAt, text });
        return;
      }
    }
    if (isEmptyNote(note)) return;
    this.lastSnapshot.set(note.id, { at: this.now(), text });
    await repo.addSnapshot(this.snapshotOf(note, 'auto')).catch(() => undefined);
  }

  async manualSnapshot(id: string, label?: string): Promise<void> {
    const note = this.state.notes[id];
    if (!note) return;
    await repo.addSnapshot(this.snapshotOf(note, 'manual', label));
    this.toast('Version saved to history.', 'success');
  }

  /** Restore a snapshot. The current content is saved as a snapshot first, so restoring is always undoable. */
  async restoreSnapshot(snap: Snapshot): Promise<void> {
    const cur = this.state.notes[snap.noteId];
    if (cur) await repo.addSnapshot(this.snapshotOf(cur, 'restore', 'Before restore'));
    const base = cur ?? snap.note;
    this.set((s) => ({
      notes: {
        ...s.notes,
        [snap.noteId]: { ...snap.note, rev: base.rev, deletedAt: null, updatedAt: this.now() },
      },
    }));
    journalPut(this.state.notes[snap.noteId]);
    await this.persist(snap.noteId);
    this.toast('Version restored. The previous text is in history.', 'success');
  }

  // ------------------------------------------------------------ settings & designs

  updateSettings(patch: Partial<Settings>): void {
    const settings = { ...this.state.settings, ...patch };
    this.set({ settings });
    void repo.saveSettings(settings).catch((e) => this.toast(`Could not save settings: ${e?.message ?? e}`, 'error'));
  }

  async saveDesign(d: Design): Promise<void> {
    await repo.putDesign(d);
    this.set((s) => ({ designs: [...s.designs.filter((x) => x.id !== d.id), d] }));
    this.broadcast({ type: 'reload' });
  }

  async deleteDesign(id: string): Promise<void> {
    await repo.deleteDesign(id);
    this.set((s) => ({ designs: s.designs.filter((x) => x.id !== id) }));
  }

  toggleFavoriteDesign(id: string): void {
    const fav = this.state.settings.favoriteDesigns;
    this.updateSettings({ favoriteDesigns: fav.includes(id) ? fav.filter((x) => x !== id) : [...fav, id] });
  }

  // ------------------------------------------------------------ schedules

  async saveSchedule(s: Schedule): Promise<void> {
    await repo.putSchedule(s);
    this.set((st) => ({ schedules: [...st.schedules.filter((x) => x.id !== s.id), s] }));
    this.refreshActive();
    this.broadcast({ type: 'reload' });
  }

  async deleteSchedule(id: string): Promise<void> {
    await repo.deleteSchedule(id);
    this.set((st) => ({ schedules: st.schedules.filter((x) => x.id !== id) }));
    this.refreshActive();
  }

  setLastSchedulerCheck(t: number): void {
    const meta = { ...this.state.meta, lastSchedulerCheck: t };
    this.state = { ...this.state, meta }; // no re-render needed
    void repo.saveMeta(meta).catch(() => undefined);
  }

  // ------------------------------------------------------------ UI feedback

  toast(text: string, tone: Toast['tone'] = 'info', action?: Toast['action']): void {
    const t: Toast = { id: uid('t'), text, tone, action };
    this.set((s) => ({ toasts: [...s.toasts.slice(-2), t] }));
    setTimeout(() => this.dismissToast(t.id), tone === 'error' ? 8000 : 4500);
  }

  dismissToast(id: string): void {
    this.set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
  }

  pushInbox(p: ReminderPayload, missed = false): void {
    this.set((s) => ({ inbox: [...s.inbox.filter((x) => x.key !== p.key), { ...p, receivedAt: this.now(), missed }] }));
  }

  dismissInbox(key: string): void {
    this.set((s) => ({ inbox: s.inbox.filter((x) => x.key !== key) }));
  }

  async reloadAll(): Promise<void> {
    const data = await repo.loadAll();
    this.set({ notes: Object.fromEntries(data.notes.map((n) => [n.id, n])), designs: data.designs, schedules: data.schedules, settings: data.settings, meta: data.meta, quarantine: data.quarantine });
    this.refreshActive();
    this.broadcast({ type: 'reload' });
  }

  async wipeEverything(): Promise<void> {
    this.timers.forEach((t) => clearTimeout(t));
    this.timers.clear();
    await repo.clearAllData();
    try {
      localStorage.clear();
    } catch {
      /* ignore */
    }
    this.lastSnapshot.clear();
    this.set({ notes: {}, designs: [], schedules: [], settings: defaultSettings(), meta: defaultMeta(), saveStatus: {}, inbox: [] });
    const first = this.createNote();
    this.setActive(first.id);
    this.broadcast({ type: 'reload' });
  }
}

export const store = new Store();

export function useStore<T>(selector: (s: AppState) => T): T {
  return useSyncExternalStore(store.subscribe, () => selector(store.get()), () => selector(store.get()));
}
